/* eslint-disable no-prototype-builtins */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { clone, is, isEmpty } from 'ramda';

import { LANDING_PAGE_ROLES } from 'constants/index';
import i18n from 'i18n';
import { transformBEScheduleToOverviewForm } from 'store/itineraryDetails/transform';
import { getDateFormat } from 'utils/date';
import { isNotDefined } from 'utils/ramda';

import { PDFDataContext } from '../../context';
import { useRecursive } from '../../functions';
import { getPreparedPageBlocks } from '../OtherPages/functions';
import s from './index.module.css';

const EC_DATA_KEY = 'emergencyContactsData';
const BLOCKS_BEFORE_TABLE_KEY = 'blocksBeforeTable';

export function getColumns() {
  return [
    {
      Header: i18n.t('proposal_content.trip_manifest.tabs.date'),
      accessor: 'date',
      width: 150,
      customProps: {
        fixedWidth: true,
      },
      Cell: ({ value: data }) => {
        if (!data?.formattedRange) {
          return <span className="tdEmpty">–</span>;
        }

        return (
          <div>
            {data.formattedRange}
            {data.formattedNumberOfNights && <div>{data.formattedNumberOfNights}</div>}
          </div>
        );
      },
    },
    {
      Header: i18n.t('proposal_content.trip_manifest.tabs.activities'),
      accessor: 'details',
      width: 420,
    },
    {
      Header: i18n.t('proposal_content.trip_manifest.tabs.accommodations'),
      accessor: 'rooming',
      Cell: ({ value }) => {
        if (isNotDefined(value)) return null;
        if (!is(String, value)) return null;

        return <div className={s.textOverflow}>{value}</div>;
      },
    },
  ];
}

export function useScheduleData({ page, onReady }) {
  const pageJSON = useMemo(() => page.landing.pages.find((p) => p.role === LANDING_PAGE_ROLES.SAFARI_MANIFEST), [page]);
  const schedules = page?.schedules;
  const scheduleType = page?.scheduleType;
  const scheduleData = useMemo(
    () =>
      transformBEScheduleToOverviewForm(scheduleType, schedules, {
        translate: true,
        translationFormat: '%B %-d',
        translationDateFormat: getDateFormat(page?.settings?.displayDatesDdMmYyyy),
      }).map((item) => {
        return {
          date: {
            formattedRange: item.formattedRange,
            formattedNumberOfNights: item.formattedNumberOfNights,
          },
          details: item.detailsText,
          rooming: item.accommodationText,
        };
      }),
    [scheduleType, schedules],
  );
  const showCondition = isEmpty(scheduleData) || !pageJSON || pageJSON.hidden;

  useEffect(() => {
    if (showCondition) {
      onReady();
    }
  }, [scheduleData]);

  return { scheduleData, pageJSON, showCondition };
}

export function useTripManifestPage({ onReady }) {
  const { page } = useContext(PDFDataContext);

  const { scheduleData, pageJSON, showCondition } = useScheduleData({ page, onReady });
  const { emergencyContactsBlocks, blocksBeforeTable, tableSubtitle, ecSubtitle } = getEmergencyContactsBlocks(
    pageJSON?.blocks,
  );
  const summaryBlocksStructure = useMemo(
    () => [
      { [BLOCKS_BEFORE_TABLE_KEY]: true, data: blocksBeforeTable },
      ...scheduleData,
      { [EC_DATA_KEY]: true, data: emergencyContactsBlocks },
    ],
    [emergencyContactsBlocks, blocksBeforeTable, scheduleData],
  );

  const [ecPageReady, setECPageReady] = useState(false);
  const [ecBlocks, setECBlocks] = useState();
  // whether was failure on the page bc Emergency Contacts block did not fit on the page
  const wasFailureRef = useRef(false);
  const handleMainReadyHandler = useCallback(() => {
    // if all the blocks fit on the page then the Trip Manifest page is ready fully
    if (!wasFailureRef.current) {
      onReady();
    }
  }, [onReady]);

  const { innerPages, handleReady, handleFail, helper } = useRecursive({
    items: summaryBlocksStructure,
    onReady: handleMainReadyHandler,
  });

  const handleFailureHandler = useCallback(
    (blocks) => {
      if (blocks.length === 1 && blocks[0][EC_DATA_KEY]) {
        wasFailureRef.current = true;
        helper.dropLastPage();
        setECBlocks(blocks[0].data);
      }

      handleFail();
    },
    [handleFail, helper],
  );

  useEffect(() => {
    if (ecPageReady) {
      onReady();
    }
  }, [onReady, ecPageReady]);

  return {
    innerPages,
    ecBlocks,
    showCondition,
    tableSubtitle,
    ecSubtitle,
    handleFailureHandler,
    setECPageReady,
    handleReady,
  };
}

export function getEmergencyContactsBlocks(blocks, prepareFn = getPreparedPageBlocks) {
  blocks = clone(blocks);
  const safariManifestBlockIdx = blocks.findIndex((block) => block.type === 'safari_manifest');
  const subtitles = blocks.filter((block) => block.type === 'subtitle');

  let ecSubtitle = '';
  let tableSubtitle = '';

  if (subtitles.length > 1) {
    ecSubtitle = subtitles[1]?.data?.text || subtitles[1]?.data?.placeholder;
    tableSubtitle = subtitles[0]?.data?.text || subtitles[0]?.data?.placeholder;
  }

  // we can do this in order to elements of subtitles is links of blocks array
  subtitles.forEach((sub) => {
    sub.type = 'header';
    sub.data = { subheadline: ecSubtitle };
  });

  const emergencyContactsBlocks = prepareFn(blocks.slice(safariManifestBlockIdx + 1));
  const blocksBeforeTable = blocks.slice(0, safariManifestBlockIdx);

  return { emergencyContactsBlocks, blocksBeforeTable, tableSubtitle, ecSubtitle };
}

export function getSeparateBlocks(blocks) {
  return blocks.reduce(
    (acc, curBlock) => {
      if (curBlock.hasOwnProperty(EC_DATA_KEY)) {
        acc[2] = curBlock.data;
      } else if (curBlock.hasOwnProperty(BLOCKS_BEFORE_TABLE_KEY)) {
        acc[0] = curBlock.data;
      } else {
        acc[1].push(curBlock);
      }

      return acc;
    },
    [[], [], undefined],
  );
}
